/**
 * Diálogo "Anular tarea del ERP" (DEVS-4204, solo Administrador). Cierra en
 * Plataforma la gestión colaborativa de un remito que no se traza en
 * RigaTraza: remitos que generaron tarea por error o que se trazaron por fuera
 * del sistema. Pide el ID de la tarea, muestra el remito para confirmar que es
 * el correcto y exige una justificación.
 *
 * Anular la tarea no anula el remito: la mercadería sale igual, así que las
 * unidades reservadas o pickeadas para el remito quedan egresadas. Si el remito
 * no sale, se anula en Plataforma (circuito de "Cancelar traza" / sync).
 */

import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAnularTareaMutation, useTareaParaAnular } from '@/api/queries';
import { fmtDate } from '@/lib/format';
import { toast } from '@/lib/notify';

/** Mismo mínimo que valida el back (`AnularTareaBodySchema`). */
const MIN_JUSTIFICACION = 10;
const MAX_JUSTIFICACION = 500;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** GLN activo: la tarea se busca dentro de ese GLN. */
  glnId: string | number | null;
}

export function AnularTareaDialog({ open, onOpenChange, glnId }: Props) {
  const [idTipeado, setIdTipeado] = useState('');
  const [erpIdBuscado, setErpIdBuscado] = useState<string | null>(null);
  const [justificacion, setJustificacion] = useState('');
  const [confirma, setConfirma] = useState(false);

  const tareaQ = useTareaParaAnular(erpIdBuscado, glnId);
  const anular = useAnularTareaMutation();
  const tarea = tareaQ.data;
  const pending = anular.isPending;

  // Cada apertura arranca vacía.
  useEffect(() => {
    if (open) {
      setIdTipeado('');
      setErpIdBuscado(null);
      setJustificacion('');
      setConfirma(false);
      anular.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al abrir
  }, [open]);

  const buscar = () => {
    const id = idTipeado.trim();
    if (!id) return;
    setErpIdBuscado(id);
    setConfirma(false);
  };

  const justificacionOk = justificacion.trim().length >= MIN_JUSTIFICACION;
  const puedeAnular =
    Boolean(tarea) && !tarea?.bloqueo && justificacionOk && confirma && !pending && !tareaQ.isFetching;

  const onAnular = () => {
    if (!tarea || !glnId) return;
    anular.mutate(
      { erpId: tarea.erpId, glnId, justificacion: justificacion.trim() },
      {
        onSuccess: (r) => {
          toast.success(
            `Tarea ${r.erpId} anulada${r.cierreErp === 'informado' ? ' en Plataforma' : ''}. ` +
              `${r.egresadas} unidad(es) egresadas.`,
          );
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const unidades = tarea ? tarea.unidades.reservadas + tarea.unidades.pickeadas : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Anular tarea del ERP</DialogTitle>
          <DialogDescription className="text-xs">
            Cierra en <b>Plataforma</b> la gestión colaborativa de un remito que no se traza en
            RigaTraza (cargado por error o trazado por fuera del sistema). El remito sigue vigente.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            buscar();
          }}
        >
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="anular-tarea-id">ID de la tarea (ACSIS)</Label>
            <Input
              id="anular-tarea-id"
              autoFocus
              value={idTipeado}
              onChange={(e) => setIdTipeado(e.target.value)}
              disabled={pending}
              placeholder="Ej. 513"
            />
          </div>
          <Button type="submit" variant="outline" disabled={pending || !idTipeado.trim()}>
            {tareaQ.isFetching ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-1 h-4 w-4" />
            )}
            Buscar
          </Button>
        </form>

        {tareaQ.isError && (
          <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800">
            {tareaQ.error.message}
          </div>
        )}

        {tarea && (
          <div className="space-y-1 rounded-md border border-gray-200 p-2 text-xs">
            <div>
              <span className="text-gray-600">Remito: </span>
              <b className="font-mono">{tarea.numeroRemito ?? '—'}</b>
              <span className="text-gray-600"> · {tarea.referencia ?? '—'}</span>
            </div>
            <div>
              <span className="text-gray-600">Entidad: </span>
              {tarea.entidad ?? '—'}
              <span className="text-gray-600"> · Creada: </span>
              {fmtDate(tarea.fechaCreacion)}
            </div>
            <div>
              <span className="text-gray-600">Unidades que egresan: </span>
              <b>{unidades}</b>
              {tarea.unidades.pickeadas > 0 && (
                <span className="text-gray-600"> ({tarea.unidades.pickeadas} pickeadas)</span>
              )}
            </div>
            {tarea.cerradaEnErp && !tarea.bloqueo && (
              <div className="text-gray-600">
                La tarea ya figura cerrada en Plataforma: solo se completa la anulación en RigaTraza.
              </div>
            )}
          </div>
        )}

        {tarea?.bloqueo && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            No se puede anular: {tarea.bloqueo.message}
          </div>
        )}

        {tarea && !tarea.bloqueo && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="anular-tarea-justificacion">
                Justificación <span className="text-red-500">* (obligatoria)</span>
              </Label>
              <textarea
                id="anular-tarea-justificacion"
                rows={3}
                maxLength={MAX_JUSTIFICACION}
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                disabled={pending}
                placeholder="Ej. Remito de destrucción de material de acondicionamiento, no se traza."
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-gray-600">
                Se envía a Plataforma como comentario del cierre. Mínimo {MIN_JUSTIFICACION}{' '}
                caracteres.
              </p>
            </div>

            <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
              Las unidades del remito quedan <b>egresadas</b>: el remito sale aunque no se trace en
              RigaTraza. Si el remito no sale, no uses esta opción: anulalo en Plataforma.
            </div>

            <label className="flex cursor-pointer items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={confirma}
                disabled={pending}
                onChange={(e) => setConfirma(e.target.checked)}
              />
              <span>
                Confirmo que se anule en Plataforma la tarea <b className="font-mono">{tarea.erpId}</b>{' '}
                del remito <b className="font-mono">{tarea.numeroRemito ?? tarea.referencia}</b>.
              </span>
            </label>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Volver
          </Button>
          <Button
            onClick={onAnular}
            disabled={!puedeAnular}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {pending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Anular tarea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
